import type {
  SchriftlichRechnenDivisionProfile,
  SchriftlichRechnenExerciseConfig,
  SchriftlichRechnenMultiplicationMethod,
  SchriftlichRechnenMultiplicationProfile,
  SchriftlichRechnenOperation,
  SchriftlichRechnenRunSettings,
} from "@calega/content";
import { randomGenerator } from "../run-random";
import { generateUniqueRunItems, normalizedItemText, type RunSelection } from "../run-selection";

export interface WrittenArithmeticTaskBase {
  id: string;
  operation: SchriftlichRechnenOperation;
  title: string;
  hint: string;
  prompt: string;
  width: number;
  expectedFields: Record<string, string>;
}

export interface WrittenArithmeticAdditionTask extends WrittenArithmeticTaskBase {
  operation: "addition";
  operator: "+";
  topDigits: string[];
  bottomDigits: string[];
  carryDigits: string[];
  resultDigits: string[];
}

export interface WrittenArithmeticSubtractionTask extends WrittenArithmeticTaskBase {
  operation: "subtraction";
  operator: "−";
  topDigits: string[];
  bottomDigits: string[];
  helperDigits: string[];
  repayDigits: string[];
  resultDigits: string[];
}

export interface WrittenArithmeticMultiplicationTask extends WrittenArithmeticTaskBase {
  operation: "multiplication";
  operator: "×";
  multiplicationMethod: SchriftlichRechnenMultiplicationMethod;
  topNumber: number;
  bottomNumber: number;
  partialRows: Array<{ factorDigit: number; shift: number; digits: string[] }>;
  additionCarryDigits: string[];
  resultDigits: string[];
}

export interface WrittenArithmeticDivisionTask extends WrittenArithmeticTaskBase {
  operation: "division";
  operator: ":";
  dividend: number;
  divisor: number;
  dividendDigits: string[];
  quotientDigits: string[];
  divisionSteps: Array<{ index: number; productRow: string[]; nextRow: string[]; separatorStart: number; separatorEnd: number }>;
}

export type WrittenArithmeticTask = WrittenArithmeticAdditionTask | WrittenArithmeticSubtractionTask | WrittenArithmeticMultiplicationTask | WrittenArithmeticDivisionTask;

export interface WrittenArithmeticRun {
  seed: string;
  generatorVersion: 1 | 2;
  settings: SchriftlichRechnenRunSettings;
  items: WrittenArithmeticTask[];
}

export interface WrittenArithmeticFieldEvent {
  seq: number;
  fieldKey: string;
  value: string;
}

export interface WrittenArithmeticEvaluation {
  correct: boolean;
  correctFieldCount: number;
  totalFieldCount: number;
  wrongFieldKeys: string[];
}

export interface WrittenArithmeticAnalysis extends WrittenArithmeticEvaluation {
  schemaVersion: "written-arithmetic-analysis-v2.1";
  correctionCount: number;
  assignmentOrder: string[];
  primaryErrorCount: number;
  consistentFollowupCount: number;
  hypotheses: Array<{
    ruleId: "written_arithmetic.carry_omitted" | "written_arithmetic.carry_misplaced" | "written_arithmetic.borrow_not_propagated" | "written_arithmetic.consistent_followup";
    status: "triggered" | "not_triggered" | "insufficient_data";
    relevantCount: number;
    evidenceCount: number;
  }>;
  columnAnalysis: Array<{ columnIndex: number; expected: number; entered: number | null; classification: "correct" | "primary" | "consistent_followup" | "other" }>;
}

const writtenMultiplicationProfiles: Record<SchriftlichRechnenMultiplicationProfile, { topDigits: number; bottomDigits: number }> = {
  "2x1": { topDigits: 2, bottomDigits: 1 },
  "3x1": { topDigits: 3, bottomDigits: 1 },
  "4x1": { topDigits: 4, bottomDigits: 1 },
  "2x2": { topDigits: 2, bottomDigits: 2 },
  "3x2": { topDigits: 3, bottomDigits: 2 },
  "4x2": { topDigits: 4, bottomDigits: 2 },
};

const writtenDivisionProfiles: Record<SchriftlichRechnenDivisionProfile, { dividendDigits: number }> = {
  "3:1": { dividendDigits: 3 },
  "4:1": { dividendDigits: 4 },
  "5:1": { dividendDigits: 5 },
};

function writtenInteger(random: () => number, min: number, max: number) {
  return min + Math.floor(random() * (max - min + 1));
}

function writtenNumberWithDigits(random: () => number, digits: number) {
  return writtenInteger(random, 10 ** (digits - 1), 10 ** digits - 1);
}

function writtenNumberWithoutZero(random: () => number, digits: number) {
  let text = "";
  while (text.length < digits) text += String(writtenInteger(random, 1, 9));
  return Number(text);
}

function writtenDigits(value: string | number) {
  return String(value).split("");
}

function writtenPaddedDigits(value: string | number, width: number) {
  return String(value).padStart(width, " ").split("").map((digit) => digit === " " ? "" : digit);
}

function writtenShiftedDigits(value: number, width: number, shift: number) {
  const digits = writtenDigits(value);
  return Array(Math.max(0, width - shift - digits.length)).fill("").concat(digits, Array(shift).fill(""));
}

function writtenAlignedDigits(value: string | number, width: number, endColumn: number) {
  const result = Array<string>(width).fill("");
  const digits = writtenDigits(value);
  const start = endColumn - digits.length + 1;
  digits.forEach((digit, index) => {
    const column = start + index;
    if (column >= 0 && column < width) result[column] = digit;
  });
  return result;
}

function writtenMultiplicationHasCarry(top: number, factor: number) {
  let carry = 0;
  for (const digit of writtenDigits(top).map(Number).reverse()) {
    const product = digit * factor + carry;
    carry = Math.floor(product / 10);
    if (product >= 10) return true;
  }
  return carry > 0;
}

function writtenAddition(random: () => number, places: 3 | 4 | 5, id: string): WrittenArithmeticAdditionTask {
  for (let attempt = 0; attempt < 2_000; attempt += 1) {
    const top = writtenNumberWithDigits(random, places);
    const bottom = writtenNumberWithDigits(random, places);
    const result = top + bottom;
    const width = Math.max(String(top).length, String(bottom).length, String(result).length) + 1;
    const topDigits = writtenPaddedDigits(top, width);
    const bottomDigits = writtenPaddedDigits(bottom, width);
    const resultDigits = writtenPaddedDigits(result, width);
    const carryDigits = Array<string>(width).fill("");
    let carry = 0;
    let carryCount = 0;
    for (let column = width - 1; column >= 0; column -= 1) {
      const sum = Number(topDigits[column] || 0) + Number(bottomDigits[column] || 0) + carry;
      carry = Math.floor(sum / 10);
      if (carry > 0 && column > 0) { carryDigits[column - 1] = String(carry); carryCount += 1; }
    }
    if (carryCount < 2) continue;
    const expectedFields: Record<string, string> = {};
    carryDigits.forEach((digit, index) => { expectedFields[`carry-${index}`] = digit; });
    resultDigits.forEach((digit, index) => { expectedFields[`result-${index}`] = digit; });
    return { id, operation: "addition", operator: "+", title: "Schriftlich addieren", hint: "Schreibe die Überträge in die kleinen Kästchen und trage unter dem Strich das Ergebnis ein.", prompt: `${top} + ${bottom}`, width, topDigits, bottomDigits, carryDigits, resultDigits, expectedFields };
  }
  throw new Error("Es konnte keine schriftliche Additionsaufgabe erzeugt werden.");
}

function writtenSubtraction(random: () => number, places: 3 | 4 | 5, id: string): WrittenArithmeticSubtractionTask {
  for (let attempt = 0; attempt < 2_000; attempt += 1) {
    const top = writtenNumberWithDigits(random, places);
    const bottom = writtenNumberWithDigits(random, places);
    if (top <= bottom) continue;
    const result = top - bottom;
    const width = Math.max(String(top).length, String(bottom).length, String(result).length) + 1;
    const topDigits = writtenPaddedDigits(top, width);
    const bottomDigits = writtenPaddedDigits(bottom, width);
    const resultDigits = writtenPaddedDigits(result, width);
    const helperDigits = Array<string>(width).fill("");
    const repayDigits = Array<string>(width).fill("");
    let repay = 0;
    let borrowCount = 0;
    for (let column = width - 1; column >= 0; column -= 1) {
      const topDigit = Number(topDigits[column] || 0);
      const bottomDigit = Number(bottomDigits[column] || 0);
      if (topDigit < bottomDigit + repay) {
        helperDigits[column] = String(topDigit + 10);
        if (column > 0) repayDigits[column - 1] = "1";
        repay = 1;
        borrowCount += 1;
      } else repay = 0;
    }
    if (!borrowCount) continue;
    const expectedFields: Record<string, string> = {};
    helperDigits.forEach((digit, index) => { expectedFields[`helper-${index}`] = digit; });
    repayDigits.forEach((digit, index) => { expectedFields[`repay-${index}`] = digit; });
    resultDigits.forEach((digit, index) => { expectedFields[`result-${index}`] = digit; });
    return { id, operation: "subtraction", operator: "−", title: "Schriftlich subtrahieren", hint: "Wenn du entbündelst, trägst du die Hilfszahl oben und die kleine 1 bei der nächsthöheren Stelle unten ein.", prompt: `${top} − ${bottom}`, width, topDigits, bottomDigits, helperDigits, repayDigits, resultDigits, expectedFields };
  }
  throw new Error("Es konnte keine schriftliche Subtraktionsaufgabe erzeugt werden.");
}

function writtenMultiplication(random: () => number, profileId: SchriftlichRechnenMultiplicationProfile, multiplicationMethod: SchriftlichRechnenMultiplicationMethod, id: string): WrittenArithmeticMultiplicationTask {
  const profile = writtenMultiplicationProfiles[profileId];
  for (let attempt = 0; attempt < 2_000; attempt += 1) {
    const top = multiplicationMethod === "place-value" ? writtenNumberWithoutZero(random, profile.topDigits) : writtenNumberWithDigits(random, profile.topDigits);
    const bottom = writtenNumberWithoutZero(random, profile.bottomDigits);
    const bottomDigits = writtenDigits(bottom);
    if (!bottomDigits.some((digit) => writtenMultiplicationHasCarry(top, Number(digit)))) continue;
    const result = top * bottom;
    const width = multiplicationMethod === "place-value"
      ? Math.max(String(bottom).length + String(top).length + 2, String(result).length + 2)
      : Math.max(String(top).length + 1 + String(bottom).length, String(result).length + 1);
    const expectedFields: Record<string, string> = {};
    const topDigits = writtenDigits(top);
    const partialCalculations = multiplicationMethod === "place-value"
      ? topDigits.flatMap((topDigit, topIndex) => bottomDigits.map((bottomDigit, bottomIndex) => ({
        factorDigit: Number(topDigit),
        shift: topDigits.length - 1 - topIndex + bottomDigits.length - 1 - bottomIndex,
        partial: Number(topDigit) * Number(bottomDigit),
      })))
      : bottomDigits.map((digit, rowIndex) => ({
        factorDigit: Number(digit),
        shift: bottomDigits.length - 1 - rowIndex,
        partial: top * Number(digit),
      }));
    const partialRows = partialCalculations.map(({ factorDigit, shift, partial }, rowIndex) => {
      const digits = writtenShiftedDigits(partial, width, shift);
      digits.forEach((value, column) => { if (value) expectedFields[`partial-${rowIndex}-${column}`] = value; });
      return { factorDigit, shift, digits };
    });
    const additionCarryDigits = Array<string>(width).fill("");
    if (partialRows.length > 1) {
      let carry = 0;
      for (let column = width - 1; column >= 0; column -= 1) {
        const sum = carry + partialRows.reduce((total, row) => total + Number(row.digits[column] || 0), 0);
        carry = Math.floor(sum / 10);
        if (carry > 0 && column > 0) additionCarryDigits[column - 1] = String(carry);
      }
      additionCarryDigits.forEach((value, column) => { expectedFields[`addition-carry-${column}`] = value; });
    }
    const resultDigits = writtenPaddedDigits(result, width);
    resultDigits.forEach((value, column) => { expectedFields[`result-${column}`] = value; });
    const placeValue = multiplicationMethod === "place-value";
    return {
      id,
      operation: "multiplication",
      operator: "×",
      multiplicationMethod,
      title: placeValue ? "Stellenweise multiplizieren" : "Schriftlich multiplizieren",
      hint: placeValue ? "Zerlege beide Faktoren in ihre Stellenwerte. Berechne alle kleinen Teilprodukte und addiere sie danach." : "Berechne zuerst die Teilprodukte. Addiere sie danach mit Übertrag zum Endergebnis.",
      prompt: placeValue ? `${bottom} × ${top}` : `${top} × ${bottom}`,
      width,
      topNumber: top,
      bottomNumber: bottom,
      partialRows,
      additionCarryDigits,
      resultDigits,
      expectedFields,
    };
  }
  throw new Error("Es konnte keine schriftliche Multiplikationsaufgabe erzeugt werden.");
}

function writtenDivision(random: () => number, profileId: SchriftlichRechnenDivisionProfile, id: string): WrittenArithmeticDivisionTask {
  const profile = writtenDivisionProfiles[profileId];
  for (let attempt = 0; attempt < 3_000; attempt += 1) {
    const divisor = writtenInteger(random, 2, 9);
    const quotient = writtenNumberWithoutZero(random, Math.max(2, profile.dividendDigits - 1));
    const dividend = quotient * divisor;
    if (String(dividend).length !== profile.dividendDigits) continue;
    const dividendText = String(dividend);
    const width = dividendText.length + 1;
    const expectedFields: Record<string, string> = {};
    const steps: WrittenArithmeticDivisionTask["divisionSteps"] = [];
    let remainder = 0;
    let started = false;
    for (let index = 0; index < dividendText.length; index += 1) {
      const current = remainder * 10 + Number(dividendText[index]);
      const digit = Math.floor(current / divisor);
      const column = width - dividendText.length + index;
      if (!started && digit === 0) { remainder = current; continue; }
      started = true;
      const product = digit * divisor;
      const nextRemainder = current - product;
      const productRow = writtenAlignedDigits(product, width, column);
      const nextValue = index < dividendText.length - 1 ? nextRemainder * 10 + Number(dividendText[index + 1]) : nextRemainder;
      const nextRow = writtenAlignedDigits(nextValue, width, Math.min(width - 1, column + (index < dividendText.length - 1 ? 1 : 0)));
      const stepIndex = steps.length;
      productRow.forEach((value, cell) => { expectedFields[`div-product-${stepIndex}-${cell}`] = value; });
      nextRow.forEach((value, cell) => { expectedFields[`div-next-${stepIndex}-${cell}`] = value; });
      steps.push({ index: stepIndex, productRow, nextRow, separatorStart: Math.max(0, productRow.findIndex(Boolean)), separatorEnd: column });
      remainder = nextRemainder;
    }
    if (!started || steps.length < 2 || remainder !== 0) continue;
    const quotientDigits = writtenDigits(quotient);
    quotientDigits.forEach((value, index) => { expectedFields[`quot-${index}`] = value; });
    return { id, operation: "division", operator: ":", title: "Schriftlich dividieren", hint: "Trage den Quotienten ein und ergänze darunter Schritt für Schritt die Minusrechnungen.", prompt: `${dividend} : ${divisor}`, width, dividend, divisor, dividendDigits: writtenPaddedDigits(dividend, width), quotientDigits, divisionSteps: steps, expectedFields };
  }
  throw new Error("Es konnte keine schriftliche Divisionsaufgabe erzeugt werden.");
}

/** Deterministische Calepia-Portierung des V1-Generators für schriftliches Rechnen. */
export function generateWrittenArithmeticRun(config: SchriftlichRechnenExerciseConfig, settings: SchriftlichRechnenRunSettings, seed: string, selection?: RunSelection): WrittenArithmeticRun {
  void config;
  const random = randomGenerator(seed);
  const target = selection?.count ?? (settings.mode === "time" ? Math.max(120, settings.durationSeconds * 2) : settings.count);
  const items = generateUniqueRunItems(target, (index) => {
    const operation = settings.operations[Math.floor(random() * settings.operations.length)]!;
    const id = `written-${index + 1}`;
    if (operation === "addition") return writtenAddition(random, settings.places, id);
    if (operation === "subtraction") return writtenSubtraction(random, settings.places, id);
    if (operation === "multiplication") return writtenMultiplication(random, settings.multiplicationProfile, settings.multiplicationMethod, id);
    return writtenDivision(random, settings.divisionProfile, id);
  }, (item) => `${item.operation}|${normalizedItemText(item.prompt)}`, random, selection?.unique)
    .map((item, index) => ({ ...item, id: `written-${index + 1}` }));
  return { seed, generatorVersion: 2, settings, items };
}

function normalizeWrittenField(value: string | undefined) {
  const clean = String(value ?? "").replace(/\s+/g, "");
  if (!clean) return "";
  return /^\d+$/.test(clean) ? String(Number(clean)) : clean;
}

function writtenFieldMatches(expected: string, actual: string | undefined) {
  const normalized = normalizeWrittenField(actual);
  return expected === "" ? normalized === "" || normalized === "0" : normalized === normalizeWrittenField(expected);
}

export function evaluateWrittenArithmeticAnswer(task: WrittenArithmeticTask, values: Record<string, string>, skipped = false): WrittenArithmeticEvaluation {
  const keys = Object.keys(task.expectedFields);
  const wrongFieldKeys = skipped ? keys : keys.filter((key) => !writtenFieldMatches(task.expectedFields[key]!, values[key]));
  return { correct: !skipped && wrongFieldKeys.length === 0, correctFieldCount: keys.length - wrongFieldKeys.length, totalFieldCount: keys.length, wrongFieldKeys };
}

function enteredWrittenDigit(values: Record<string, string>, key: string) {
  const value = normalizeWrittenField(values[key]);
  return /^\d+$/.test(value) ? Number(value) : null;
}

export function analyzeWrittenArithmeticAnswer(task: WrittenArithmeticTask, values: Record<string, string>, events: WrittenArithmeticFieldEvent[], skipped = false): WrittenArithmeticAnalysis {
  const evaluation = evaluateWrittenArithmeticAnswer(task, values, skipped);
  const eventCounts = new Map<string, number>();
  const firstSequence = new Map<string, number>();
  for (const event of [...events].sort((left, right) => left.seq - right.seq)) {
    eventCounts.set(event.fieldKey, (eventCounts.get(event.fieldKey) ?? 0) + 1);
    if (event.value.trim() && !firstSequence.has(event.fieldKey)) firstSequence.set(event.fieldKey, event.seq);
  }
  const correctionCount = [...eventCounts.values()].reduce((sum, count) => sum + Math.max(0, count - 1), 0);
  const assignmentOrder = [...firstSequence.entries()].sort((left, right) => left[1] - right[1]).map(([key]) => key);
  const columnAnalysis: WrittenArithmeticAnalysis["columnAnalysis"] = [];
  let carryOmitted = 0;
  let carryMisplaced = 0;
  let borrowNotPropagated = 0;
  let followups = 0;
  let transitionCount = 0;
  const followupOpportunityColumns = new Set<number>();

  if (task.operation === "addition" || task.operation === "subtraction") {
    const primaryColumns: Array<{ column: number; childTransition: number }> = [];
    for (let column = task.width - 1; column >= 0; column -= 1) {
      const top = Number(task.topDigits[column] || 0);
      const bottom = Number(task.bottomDigits[column] || 0);
      const expected = Number(task.resultDigits[column] || 0);
      const entered = enteredWrittenDigit(values, `result-${column}`);
      let classification: WrittenArithmeticAnalysis["columnAnalysis"][number]["classification"] = entered === expected ? "correct" : "other";
      if (task.operation === "addition") {
        const expectedCarry = Number(task.carryDigits[column] || 0);
        if (expectedCarry) {
          transitionCount += 1;
          const withoutCarry = (top + bottom) % 10;
          if (entered === withoutCarry && entered !== expected) {
            primaryColumns.push({ column, childTransition: Math.floor((top + bottom) / 10) });
            classification = "primary";
            const actualCarry = enteredWrittenDigit(values, `carry-${column}`);
            const misplaced = task.carryDigits.some((digit, otherColumn) => otherColumn !== column && !digit && enteredWrittenDigit(values, `carry-${otherColumn}`) === 1);
            if (actualCarry !== 1 && misplaced) carryMisplaced += 1;
            else carryOmitted += 1;
          }
        }
      } else {
        const expectedBorrow = Number(task.repayDigits[column] || 0);
        if (expectedBorrow) {
          transitionCount += 1;
          let effective = top;
          if (effective < bottom) effective += 10;
          const withoutBorrow = effective - bottom;
          if (entered === withoutBorrow && entered !== expected) {
            primaryColumns.push({ column, childTransition: top < bottom ? 1 : 0 });
            classification = "primary";
            borrowNotPropagated += 1;
          }
        }
      }
      columnAnalysis.push({ columnIndex: column, expected, entered, classification });
    }
    for (const primary of primaryColumns) {
      let childTransition = primary.childTransition;
      for (let column = primary.column - 1; column >= 0; column -= 1) {
        const row = columnAnalysis.find((entry) => entry.columnIndex === column);
        if (!row || row.entered === null || row.classification === "primary") break;
        followupOpportunityColumns.add(column);
        const top = Number(task.topDigits[column] || 0);
        const bottom = Number(task.bottomDigits[column] || 0);
        let childResult: number;
        if (task.operation === "addition") {
          const sum = top + bottom + childTransition;
          childResult = sum % 10;
          childTransition = Math.floor(sum / 10);
        } else {
          let effectiveTop = top - childTransition;
          const nextBorrow = effectiveTop < bottom ? 1 : 0;
          if (nextBorrow) effectiveTop += 10;
          childResult = effectiveTop - bottom;
          childTransition = nextBorrow;
        }
        if (row.classification === "correct" || row.entered !== childResult) break;
        if (row.classification !== "consistent_followup") {
          row.classification = "consistent_followup";
          followups += 1;
        }
      }
    }
  }
  const hypothesis = (ruleId: WrittenArithmeticAnalysis["hypotheses"][number]["ruleId"], relevantCount: number, evidenceCount: number) => ({ ruleId, relevantCount, evidenceCount, status: relevantCount === 0 ? "insufficient_data" as const : evidenceCount > 0 ? "triggered" as const : "not_triggered" as const });
  return {
    ...evaluation,
    schemaVersion: "written-arithmetic-analysis-v2.1",
    correctionCount,
    assignmentOrder,
    primaryErrorCount: carryOmitted + carryMisplaced + borrowNotPropagated,
    consistentFollowupCount: followups,
    hypotheses: [
      hypothesis("written_arithmetic.carry_omitted", task.operation === "addition" ? transitionCount : 0, carryOmitted),
      hypothesis("written_arithmetic.carry_misplaced", task.operation === "addition" ? transitionCount : 0, carryMisplaced),
      hypothesis("written_arithmetic.borrow_not_propagated", task.operation === "subtraction" ? transitionCount : 0, borrowNotPropagated),
      hypothesis("written_arithmetic.consistent_followup", followupOpportunityColumns.size, followups),
    ],
    columnAnalysis: columnAnalysis.sort((left, right) => left.columnIndex - right.columnIndex),
  };
}
